import { Box, Button, Skeleton, Stack, Typography } from "@mui/material";

import { useState } from "react";

import { skipToken } from "@reduxjs/toolkit/query";

import { useGetCheckInSummaryQuery } from "app/containers/TeamsTab/teamsCheckInApi";

import {
  useActiveCheckInConversation,
  useOpenConversation,
  useStartNewChat,
} from "../../../common";

import { LargeCustomIcon } from "../../layer00";

import { BoZoneCallout, StyledCard } from "../../layer01";

import { OneOnOnePrepModal } from "../../layer16";

import boroscope from "../../../../assets/images/boroscope.png";

type BoZoneFocusCardProps = {
  userAccountId: number | null;
};

// Image + text side by side, actions wrap onto their own row.
const SPLIT_QUERY = "@container (min-width: 26rem)";
// Image, text and actions all on one row.
const INLINE_QUERY = "@container (min-width: 42rem)";

const TitleSkeleton = () => <Skeleton width="50%" height={24} />;

const BodySkeleton = () => (
  <Stack direction="column" rowGap=".25rem">
    <Skeleton width="95%" height={16} />
    <Skeleton width="85%" height={16} />
    <Skeleton width="60%" height={16} />
  </Stack>
);

export const BoZoneFocusCard = (props: BoZoneFocusCardProps) => {
  const [isPrepVisible, setIsPrepVisible] = useState(false);
  const openConversation = useOpenConversation();

  const { data, isFetching } = useGetCheckInSummaryQuery(
    props.userAccountId !== null
      ? {
          userAccountId: props.userAccountId,
          mostRecentCompletedCheckIn: true,
        }
      : skipToken
  );

  const completedSummary = data?.checkInCompleted ? data : null;
  const nextWeekWork = completedSummary?.nextWeekWork ?? null;
  const hasPendingCheckIn = !!data?.hasPendingCheckIn;

  const pendingCheckInConversation = useActiveCheckInConversation(
    props.userAccountId
  );

  const handleLetsChat = useStartNewChat();

  const handleCheckIn = () => {
    if (pendingCheckInConversation) {
      openConversation(pendingCheckInConversation);
    }
  };

  const getTitle = () => {
    if (nextWeekWork) {
      return completedSummary?.primaryTheme ?? "Your Current Focus";
    }
    if (hasPendingCheckIn) {
      return "Your check-in is waiting";
    }
    return "No focus set yet";
  };

  const getBody = () => {
    if (nextWeekWork) {
      return nextWeekWork;
    }
    if (hasPendingCheckIn) {
      return "Finish your check-in to set what you're focusing on next week.";
    }
    return "Chat with Bo to figure out what you're focusing on next.";
  };

  return (
    <StyledCard title="What You're Focusing On">
      <BoZoneCallout>
        {/* Container queries rather than viewport breakpoints: the card's width
            depends on whether the nav bar and settings panel are open. */}
        <Box sx={{ containerType: "inline-size" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              columnGap: "1.5rem",
              rowGap: "1rem",
              [SPLIT_QUERY]: {
                flexDirection: "row",
                flexWrap: "wrap",
                alignItems: "center",
              },
              [INLINE_QUERY]: {
                flexWrap: "nowrap",
              },
            }}
          >
            <Box
              component="img"
              src={boroscope}
              sx={{
                alignSelf: "center",
                flexShrink: 0,
                width: "8rem",
                height: "8rem",
                mixBlendMode: "darken",
              }}
            />
            <Stack
              direction="column"
              rowGap=".5rem"
              minWidth={0}
              sx={{
                textAlign: "center",
                [SPLIT_QUERY]: {
                  flex: "1 1 0",
                  textAlign: "left",
                },
              }}
            >
              {isFetching ? (
                <TitleSkeleton />
              ) : (
                <Typography variant="cardSectionTitle">{getTitle()}</Typography>
              )}
              {isFetching ? (
                <BodySkeleton />
              ) : (
                <Typography
                  variant="cardBodyRegular"
                  fontSize=".8125rem"
                  color="#202D63"
                >
                  {getBody()}
                </Typography>
              )}
            </Stack>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: ".75rem",
                [SPLIT_QUERY]: {
                  flexDirection: "row",
                  flexBasis: "100%",
                  // Line the wrapped actions up under the text (image + gap)
                  paddingLeft: "9.5rem",
                },
                [INLINE_QUERY]: {
                  flexBasis: "auto",
                  flexShrink: 0,
                  paddingLeft: 0,
                },
              }}
            >
              <Button
                variant="outlined"
                onClick={() => setIsPrepVisible(() => true)}
                sx={{
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                }}
              >
                Open Your 1:1
              </Button>
              {!nextWeekWork && hasPendingCheckIn ? (
                <Button
                  variant="contained"
                  onClick={handleCheckIn}
                  disabled={!pendingCheckInConversation}
                  sx={{
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                  }}
                  startIcon={
                    <LargeCustomIcon icon="SPARKLE" stroke="#FFFFFF" />
                  }
                >
                  Check In
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleLetsChat}
                  sx={{
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                  }}
                  startIcon={
                    <LargeCustomIcon icon="SPARKLE" stroke="#FFFFFF" />
                  }
                >
                  Let&rsquo;s chat
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </BoZoneCallout>
      {isPrepVisible && (
        <OneOnOnePrepModal
          id={`bo-zone-focus-card-${props.userAccountId}`}
          isVisible
          userAccountId={props.userAccountId}
          perspective="self"
          handleClose={() => setIsPrepVisible(() => false)}
          mostRecentCompletedCheckIn
        />
      )}
    </StyledCard>
  );
};
